import React from 'react';
import NextImage from 'next/image';
import { MY_YOU_PAGE } from 'constants/pages';
import { DEFAULT_TEAM_BOUNCE_LINK } from 'constants/pages';
import toTitleCase from 'utils/shared/string/toTitleCase';
import { useAuthModals } from 'hooks/useAuthModals';
import { useModal } from 'hooks/useModal';
import { useViewer } from 'hooks/useViewer';
import Academia from 'svg/Academia';
import ChevronLeft from 'svg/ChevronLeft';
import Share from 'svg/Share';
import Users from 'svg/Users';
import { ButtonText } from 'components/Button';
import Link from 'components/Link';
import ModalShare from 'components/modals/ModalShare';
import CommunityBadge from './CommunityBadge';
import DetailCard from './DetailCard';
import { GroupPageMobProps } from './types';

function GroupPageMob({
  items,
  group,
  handleMailtoClick,
  DEFAULT_GROUP_IMAGE,
  clickedSignUp,
}: GroupPageMobProps) {
  const membersDisplayCount = group?.memberDisplayCount || 0;
  const membersAggregateCount = group?.membersAggregate?.aggregate?.count || 0;

  const membersCount =
    membersDisplayCount > membersAggregateCount ? membersDisplayCount : membersAggregateCount;

  let rankSize = 5;

  if (membersCount < 10) {
    rankSize = 12;
  } else if (membersCount < 20) {
    rankSize = 9;
  } else if (membersCount < 50) {
    rankSize = 7;
  } else if (membersCount < 100) {
    rankSize = 4;
  } else if (membersCount < 200) {
    rankSize = 2;
  }

  const {
    openModal: openShareModal,
    closeModal: closeShareModal,
    isOpen: isShareModalOpen,
  } = useModal();

  const { isAnonymousSession } = useViewer();
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const shareAction = () => {
    openShareModal();
    if (navigator.share) {
      navigator
        .share({
          title: `Share groups on Bounce`,
          text: `More details at ${shareUrl}`,
          url: shareUrl,
        })
        .then(() => console.log('Successful share'))
        .catch((error) => console.log('Error sharing:', error));
    }
  };

  return (
    <main className="mx-auto flex h-full w-full max-w-[70rem] grow flex-col bg-color-bg-lightmode-primary dark:bg-color-bg-darkmode-primary">
      <div className="flex flex-row justify-between pb-2 pt-4">
        <Link
          className="flex cursor-pointer items-center justify-center rounded-3xl bg-color-bg-lightmode-primary p-2 dark:bg-color-bg-darkmode-primary"
          href={MY_YOU_PAGE}
        >
          <ChevronLeft className="w-6" />
        </Link>
        <div className="flex flex-row justify-between gap-4">
          <ButtonText
            size="sm"
            className="flex cursor-pointer items-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary"
            onClick={shareAction}
          >
            <Share className="mr-1 h-4 w-4" /> Share
          </ButtonText>
        </div>
      </div>
      <div className="flex flex-row gap-4">
        <div className="flex w-1/3 items-center	">
          <NextImage
            src={group?.profilePhotoUrl || DEFAULT_GROUP_IMAGE}
            alt={group?.profilePhotoUrl}
            height={960}
            width={512}
          />
        </div>

        <div className="flex w-8/12 flex-col gap-6">
          <div className="flex items-center text-xs text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
            <div className="items-container flex flex-col justify-start gap-2">
              <h1 className="typography-product-subheading whitespace-normal text-color-text-lightmode-primary dark:text-color-text-darkmode-primary md:mt-ds-xl	">
                {toTitleCase(group?.title || '')}
              </h1>
              <CommunityBadge label={toTitleCase(group?.type)} variant={group?.type} size="sm" />

              {items.map((item, index) => (
                <div key={index} className="flex flex-row items-center justify-start gap-1">
                  <span>{item.text ? item.svg : ''}</span>

                  <p className="typography-product-text-card text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
                    {typeof item.text === 'string' && index === 1 ? (
                      <span className="typography-product-button-label-xs flex gap-1">
                        {item.text}
                        <ButtonText
                          className="cursor-pointer bg-color-bg-lightmode-primary underline underline-offset-4 dark:bg-color-bg-darkmode-primary"
                          onClick={() => {
                            if (isAnonymousSession) {
                              clickedSignUp && clickedSignUp();
                            } else {
                              handleMailtoClick();
                            }
                          }}
                        >
                          contact me
                        </ButtonText>
                      </span>
                    ) : (
                      <>{item.text}</>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="my-2 flex flex-col gap-3">
        <p className="typography-product-caption py-2 text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary ![overflow-wrap:anywhere] break-normal">
          {group?.description || ''}
        </p>
        <div className="grid grid-cols-2 gap-ds-sm">
          <DetailCard
            Icon={Users}
            description={membersCount?.toString()}
            title="Total group members"
          />
          <DetailCard Icon={Academia} description={`${rankSize}%`} title="Rank by size" />
        </div>
      </div>

      <ModalShare
        isOpen={isShareModalOpen}
        closeModal={closeShareModal}
        shareUrl={shareUrl}
        title="Share this link"
      />
    </main>
  );
}

export default GroupPageMob;
